type Token = number | string;

const functions: Record<string, (value: number) => number> = {
  sqrt: Math.sqrt,
  log: Math.log10,
  ln: Math.log,
  sin: (value) => Math.sin((value * Math.PI) / 180),
  cos: (value) => Math.cos((value * Math.PI) / 180),
  tan: (value) => Math.tan((value * Math.PI) / 180)
};

const precedence: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2, '%': 2, '^': 3 };

export function evaluateExpression(expression: string): number {
  const tokens = tokenize(expression);
  const output: Token[] = [];
  const operators: string[] = [];

  for (const token of tokens) {
    if (typeof token === 'number') output.push(token);
    else if (token in functions) operators.push(token);
    else if (token === '(') operators.push(token);
    else if (token === ')') {
      while (operators.length && operators[operators.length - 1] !== '(') output.push(operators.pop() as string);
      if (operators.pop() !== '(') throw new Error('Mismatched parentheses');
      if (operators.length && operators[operators.length - 1] in functions) output.push(operators.pop() as string);
    } else {
      while (
        operators.length &&
        operators[operators.length - 1] !== '(' &&
        (precedence[operators[operators.length - 1]] > precedence[token] || (precedence[operators[operators.length - 1]] === precedence[token] && token !== '^'))
      ) {
        output.push(operators.pop() as string);
      }
      operators.push(token);
    }
  }

  while (operators.length) {
    const operator = operators.pop() as string;
    if (operator === '(') throw new Error('Mismatched parentheses');
    output.push(operator);
  }

  const stack: number[] = [];
  for (const token of output) {
    if (typeof token === 'number') stack.push(token);
    else if (token in functions) {
      const value = stack.pop();
      if (value === undefined) throw new Error('Invalid expression');
      stack.push(functions[token](value));
    } else {
      const right = stack.pop();
      const left = stack.pop();
      if (left === undefined || right === undefined) throw new Error('Invalid expression');
      stack.push(applyOperator(left, right, token));
    }
  }

  if (stack.length !== 1 || !Number.isFinite(stack[0])) throw new Error('Invalid expression');
  return Number(stack[0].toPrecision(12));
}

function tokenize(expression: string): Token[] {
  const tokens: Token[] = [];
  const normalized = expression.replace(/×/g, '*').replace(/÷/g, '/').replace(/\s+/g, '');
  let index = 0;
  while (index < normalized.length) {
    const char = normalized[index];
    if (/[0-9.]/.test(char) || (char === '-' && (index === 0 || ['(', '+', '-', '*', '/', '^', '%'].includes(String(tokens[tokens.length - 1]))))) {
      const match = normalized.slice(index).match(/^-?\d*\.?\d+/);
      if (!match) throw new Error('Invalid number');
      tokens.push(Number(match[0]));
      index += match[0].length;
    } else if (/[a-z]/i.test(char)) {
      const match = normalized.slice(index).match(/^[a-z]+/i);
      if (!match || !(match[0] in functions)) throw new Error('Unsupported function');
      tokens.push(match[0]);
      index += match[0].length;
    } else if ('+-*/^%()'.includes(char)) {
      tokens.push(char);
      index += 1;
    } else {
      throw new Error('Unsupported character');
    }
  }
  return tokens;
}

function applyOperator(left: number, right: number, operator: string): number {
  if (operator === '+') return left + right;
  if (operator === '-') return left - right;
  if (operator === '*') return left * right;
  if (operator === '/') return left / right;
  if (operator === '%') return (left / 100) * right;
  if (operator === '^') return left ** right;
  throw new Error('Unsupported operator');
}
